import type { ReactNode } from "react";
import { Banner } from "@astryxdesign/core/Banner";
import { Button } from "@astryxdesign/core/Button";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { VStack } from "@astryxdesign/core/VStack";
import { errorMessage } from "@/lib/errors";
import { useT } from "@/lib/i18n";

/** Shape-matching placeholder rows while a list loads. */
export function LoadingRows({ rows = 4, height = 64, label }: { rows?: number; height?: number; label?: string }) {
  const t = useT();
  return (
    <VStack gap={2} aria-busy="true" aria-label={label ?? t.common.loading} role="status">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} index={i} height={height} radius={3} />
      ))}
    </VStack>
  );
}

/** Network/server error with a retry that actually refetches. */
export function ErrorState({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  const t = useT();
  return (
    <Banner
      status="error"
      title={title ?? t.common.notLoaded}
      description={errorMessage(error)}
      collapsible={false}
      endContent={onRetry ? <Button label={t.common.retry} size="sm" onClick={onRetry} /> : undefined}
    />
  );
}

export function Empty({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return <EmptyState title={title} description={description} actions={action} isCompact />;
}
